import {
  type JSX, type ReactNode, useCallback, useEffect, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { IMaintenanceWindow } from '@zero-dash/types';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';
import { useUser } from '@/contexts/UserContext';
import { MaintenanceProvider, clearMaintenanceDismissals } from '@/contexts/MaintenanceContext';
import MaintenanceGate from '@/components/Maintenance/MaintenanceGate';
import { MAINTENANCE_POLL_INTERVAL_MS } from '@/constants/Maintenance/maintenance';
import { LoadingPage } from '@/pages/Loading/Loading';

interface IProps {
  children: ReactNode;
}

export function MaintenanceProviderConnected({ children }: IProps): JSX.Element {
  const sdk = useZeroDashSdk();
  const { isAdmin } = useUser();
  const location = useLocation();
  const [maintenanceWindow, setMaintenanceWindow] = useState<IMaintenanceWindow | null>(null);

  const [initialised, setInitialised] = useState(false);

  const isTrackingMaintenance = maintenanceWindow !== null;

  const fetchStatus = useCallback((signal: AbortSignal): Promise<void> => (
    sdk.services.maintenance.getEvent(signal)
      .then((activeWindow) => {
        if (signal.aborted) return;
        setMaintenanceWindow(activeWindow);
        if (!activeWindow) clearMaintenanceDismissals();
      })
      .catch(() => undefined)
  ), [sdk]);

  // Fetch on load and re-read on navigation (no polling for discovery).
  useEffect(() => {
    const controller = new AbortController();
    fetchStatus(controller.signal).finally(() => {
      if (!controller.signal.aborted) setInitialised(true);
    });
    return () => controller.abort();
  }, [fetchStatus, location.pathname]);

  useEffect(() => {
    if (!isTrackingMaintenance) return undefined;

    const controller = new AbortController();
    const intervalId = window.setInterval(() => {
      fetchStatus(controller.signal);
    }, MAINTENANCE_POLL_INTERVAL_MS);

    return () => {
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, [isTrackingMaintenance, fetchStatus]);

  if (!initialised) return <LoadingPage />;

  return (
    <MaintenanceProvider maintenanceWindow={maintenanceWindow} isAdmin={isAdmin}>
      <MaintenanceGate>{children}</MaintenanceGate>
    </MaintenanceProvider>
  );
}
